import { OptimizePlanRequest, OptimizePlanResponse, ProductionPlanService } from "@/api"
import { useMutationTaskWithProgress } from "@/api/hooks/useTaskWithProgress"
import ResultWithProgress from "@/components/ResultWithProgress"
import { Button } from "primereact/button"
import { useState, useEffect } from "react";
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'

type EchoAlgorithm = 'v6' | 'v7'

// 画面で既定にする最適化の方式 (backtest の合格判定で決める。V7_HANDOFF.md T4)
const DEFAULT_ALGORITHM: EchoAlgorithm = 'v6'

const ALGORITHM_OPTIONS: { value: EchoAlgorithm, label: string, description: string }[] = [
  {
    value: 'v7',
    label: '新方式（V7）',
    description: 'これまでの手入力の計画から、ロットの長さ・機械・休日の出し方を学んで計画します。休日出勤と段取りが少なめになります。'
  },
  {
    value: 'v6',
    label: '旧方式（V6）',
    description: 'これまでの自動計画です。欠品を防ぐために休日も広く使います。'
  },
]


interface OptimizerProps {
  originalResponse?: OptimizePlanResponse,
  optimizeRequest: OptimizePlanRequest,
  onBack(): void;
  onProceed(response: OptimizePlanResponse, request: OptimizePlanRequest): void;
}

const Optimizer = ({optimizeRequest,onBack,onProceed} : OptimizerProps) => {
  const [optimizeMethod, setOptimizeMethod] = useState<OptimizePlanRequest.optimize>(optimizeRequest.optimize);
  const [algorithm, setAlgorithm] = useState<EchoAlgorithm>(optimizeRequest.algorithm ?? DEFAULT_ALGORITHM)
  // ロック (既定はロックなし。2026-10-13 決定)
  const workdays = optimizeRequest.days.filter((d) => (d.capacity?.capacity?.value ?? 0) > 0).map((d) => d.date)
  const machines = optimizeRequest.days[0]?.capacity?.lines ?? []
  const [lockEnabled, setLockEnabled] = useState<boolean>(Boolean(optimizeRequest.lock))
  const [lockUntil, setLockUntil] = useState<string>(optimizeRequest.lock?.until ?? workdays[2] ?? workdays[0] ?? '')
  const [lockMachines, setLockMachines] = useState<string[]>(optimizeRequest.lock?.machines ?? [])
  const lock = algorithm === 'v7' && lockEnabled && lockUntil
    ? { until: lockUntil, machines: lockMachines.length ? lockMachines : null }
    : null

  const request: OptimizePlanRequest = {
    ...optimizeRequest,
    optimize: optimizeMethod,
    algorithm,
    lock,
  }

  // 「最適化を開始」を押したときだけ実行する (設定を選んでいる途中で V6 などが走らないように)
  const [runRequest, setRunRequest] = useState<OptimizePlanRequest | null>(null)
  const optimizeMutation = useMutationTaskWithProgress((req: OptimizePlanRequest) => ProductionPlanService.optimize(req))
  const { isPending, isSuccess, isError, error, data, progress, lastLog, logs, isObserving, reset } = optimizeMutation
  const settingsKey = JSON.stringify([optimizeMethod, algorithm, lock])

  // 実行後に設定を変えたら、前の結果は使えないので消す
  useEffect(() => {
    if (!isPending) {
      reset()
      setRunRequest(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsKey])

  const handleStart = () => {
    setRunRequest(request)
    optimizeMutation.mutate([request])
  }

  const handleBack = () => {
    onBack()
  }

  const handleProceed = () => {
    if (isSuccess && data && runRequest) {
      onProceed(data, runRequest)
    }
  }

  return (
    <div className="mt-6">
      <div className="mb-4">
        <h3>最適化方法の選択</h3>
        <div className="flex flex-column gap-3 mt-3">
          <div className="flex align-items-center">
            <RadioButton
              inputId="optimize_method4"
              name="optimize_method"
              value="optimize"
              onChange={(e) => setOptimizeMethod(e.value)}
              disabled={isPending}
              checked={optimizeMethod === "optimize"}
            />
            <label htmlFor="optimize_method4" className="ml-2">通常最適化</label>
          </div>
          <div className="flex align-items-center">
            <RadioButton
              inputId="optimize_method5"
              name="optimize_method"
              value="simulation_only"
              onChange={(e) => setOptimizeMethod(e.value)}
              disabled={isPending}
              checked={optimizeMethod === "simulation_only"}
            />
            <label htmlFor="optimize_method5" className="ml-2">シミュレーションのみ（最適化なし）</label>
          </div>
        </div>
        {optimizeMethod === OptimizePlanRequest.optimize.OPTIMIZE && (
          <>
            <h3 className="mt-5">計画の方式</h3>
            <div className="flex flex-column gap-3 mt-3">
              {ALGORITHM_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex align-items-start">
                  <RadioButton
                    inputId={`algorithm_${opt.value}`}
                    name="algorithm"
                    value={opt.value}
                    onChange={(e) => setAlgorithm(e.value)}
                    disabled={isPending}
                    checked={algorithm === opt.value}
                  />
                  <label htmlFor={`algorithm_${opt.value}`} className="ml-2">
                    <div>{opt.label}{opt.value === DEFAULT_ALGORITHM && '（既定）'}</div>
                    <div className="text-sm text-color-secondary mt-1">{opt.description}</div>
                  </label>
                </div>
              ))}
            </div>
            {algorithm === 'v7' && (
              <div className="mt-4">
                <div className="flex align-items-center">
                  <Checkbox inputId="lock_enabled" checked={lockEnabled} onChange={(e) => setLockEnabled(Boolean(e.checked))} disabled={isPending} />
                  <label htmlFor="lock_enabled" className="ml-2">Excel 上の成形を固定する（ロック）</label>
                </div>
                <div className="text-sm text-color-secondary mt-1 ml-5">
                  計画の最初から選んだ日までは、Excel に入っている成形をそのまま残し、新方式はその後ろから計画します。
                  Excel に保存するときも、ロックした日は書き換えません。
                </div>
                {lockEnabled && (
                  <div className="flex flex-row flex-wrap gap-3 mt-2 ml-5 align-items-center">
                    <span>この日まで</span>
                    <Dropdown value={lockUntil} options={workdays} onChange={(e) => setLockUntil(e.value)} className="p-inputtext-sm" disabled={isPending} />
                    <MultiSelect
                      value={lockMachines}
                      options={machines}
                      onChange={(e) => setLockMachines(e.value)}
                      placeholder="全ての機械"
                      className="p-inputtext-sm"
                      display="chip"
                      disabled={isPending}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-row justify-content-center mb-3">
        <Button label={isSuccess ? "この設定でもう一度実行" : "最適化を開始"} icon="pi pi-play"
          onClick={handleStart} loading={isPending} disabled={isPending} />
      </div>
      {(runRequest || isPending) && (
        <ResultWithProgress
          isObserving={isPending || isObserving}
          progress={progress}
          lastLog={lastLog}
          logs={logs}
          isError={isError}
          error={error}>
        </ResultWithProgress>
      )}
      <div className="flex flex-row justify-content-between mt-6">
        <Button label="戻る" icon="pi pi-arrow-left"
          iconPos="left" size="small" severity='secondary'
          onClick={handleBack} />
        <Button label="次へ" icon="pi pi-arrow-right"
          iconPos="right" severity='success'
          disabled={!isSuccess || !data || isPending}
          onClick={handleProceed} />
      </div>
    </div>
  )
}

export default Optimizer
